import {
  assertDatabaseImportPreflightAllowsInlineShardedMutation,
  assertDatabaseImportPreflightAllowsShardedMutation,
  DATABASE_PRODUCT_LIMITS,
  type DatabaseImportPreflightEstimate,
  DOCUMENT_PRODUCT_HARD_LIMIT_BYTES,
  estimateDatabaseImportPreflight,
} from '@streamlain/database-identity';

import { streamCsvRows } from './csv-row-stream';
import { IMPORT_ROW_ID_LENGTH } from './database-import-payload';
import { readExcelWorkbookBytes } from './excel-workbook-bytes';
import { excelWorkbookReader } from './excel-workbook-reader';
import {
  type ImportLimitDetails,
  ImportLimitError,
} from './import-limit-error';
import type { BaseColumnDef } from './types';
import {
  columnName,
  detectColumnType,
  isCheckbox,
  isDate,
  isNumber,
  isUrl,
} from './utils';

const PREVIEW_ROW_LIMIT = 10;
const TYPE_SAMPLE_ROW_LIMIT = 100;
const EXCEL_ROWS_TO_READ = TYPE_SAMPLE_ROW_LIMIT + 1;
const COLUMN_LIMIT = DATABASE_PRODUCT_LIMITS.columnHardLimit;
const ROW_LIMIT = DATABASE_PRODUCT_LIMITS.rowHardLimit;
const EXCEL_COLUMNS_TO_READ = COLUMN_LIMIT;

/** Shape limits of one source; cells are checked when the import starts. */
function shapeLimit(
  rowCount: number,
  columnCount: number
): ImportLimitDetails | undefined {
  if (rowCount === 0 || columnCount === 0)
    return { kind: 'empty', actual: 0, limit: 1 };
  if (columnCount > COLUMN_LIMIT)
    return { kind: 'columns', actual: columnCount, limit: COLUMN_LIMIT };
  if (rowCount > ROW_LIMIT)
    return { kind: 'rows', actual: rowCount, limit: ROW_LIMIT };
  return undefined;
}

export type ImportPreflightColumn = BaseColumnDef;

export type ImportSheetPreflight = {
  source: string;
  fileName: string;
  sheetName?: string;
  columns: ImportPreflightColumn[];
  previewRows: string[][];
  estimate: DatabaseImportPreflightEstimate;
  limit?: ImportLimitDetails;
};

export type ImportFilePreflight = {
  fileName: string;
  sheets: ImportSheetPreflight[];
};

export type ImportPreflightProgressPhase =
  | 'file-reading'
  | 'csv-reading'
  | 'workbook-reading'
  | 'workbook-loaded'
  | 'sheet-scanning'
  | 'preflight-complete';

export type ImportPreflightProgress = {
  phase: ImportPreflightProgressPhase;
  fileName: string;
  sheetName?: string;
  processedRows?: number;
  totalRows?: number;
};

export type ImportPreflightProgressHandler = (
  progress: ImportPreflightProgress
) => void;

export type CsvPreflightOptions = {
  file: File;
  signal?: AbortSignal;
  hasHeader?: boolean;
  onProgress?: ImportPreflightProgressHandler;
};

export type ExcelPreflightOptions = {
  file: File;
  hasHeader?: boolean;
  selectedSheets?: readonly string[];
  onProgress?: ImportPreflightProgressHandler;
};

function createColumnsFromSamples(
  headerRow: readonly unknown[] | null,
  columnCount: number,
  sampleRows: readonly (readonly unknown[])[],
  inferredTypes?: ImportPreflightColumn['type'][]
): ImportPreflightColumn[] {
  return Array.from({ length: columnCount }, (_, index) => {
    const headerValue = headerRow?.[index];
    const name =
      headerValue != null && String(headerValue).trim() !== ''
        ? String(headerValue).trim()
        : columnName(index);
    const values = sampleRows
      .map(row => row[index])
      .filter(value => value != null && String(value).trim() !== '');

    return {
      id: `preflight-column-${index}`,
      name,
      type: inferredTypes?.[index] ?? detectColumnType(values),
    };
  });
}

function stringifyRows(rows: readonly (readonly unknown[])[]): string[][] {
  return rows.map(row => row.map(cell => String(cell ?? '')));
}

export async function preflightCsvFile({
  file,
  hasHeader = true,
  onProgress,
  signal,
}: CsvPreflightOptions): Promise<ImportSheetPreflight> {
  onProgress?.({ phase: 'file-reading', fileName: file.name });
  onProgress?.({ phase: 'csv-reading', fileName: file.name });
  let rawRowIndex = 0;
  let dataRowCount = 0;
  let columnCount = 0;
  let headerRow: string[] | null = null;
  const previewRows: string[][] = [];
  const typeSampleRows: string[][] = [];
  const encoder = new TextEncoder();
  let previewBytes = 0;
  let sampleBytes = 0;
  // Preserve the extractor's first 100 nonempty values per column without
  // retaining their strings or coupling persisted types to the preview budget.
  const types: Array<{
    count: number;
    checkbox: boolean;
    number: boolean;
    date: boolean;
    link: boolean;
  }> = [];
  for await (const row of streamCsvRows(file, { signal })) {
    columnCount = Math.max(columnCount, row.length);
    // Keep the existing blocked-preflight result for the preview UI, but stop
    // scanning immediately instead of retaining an arbitrarily wide schema.
    if (hasHeader && rawRowIndex++ === 0) {
      headerRow = row;
      continue;
    }
    dataRowCount++;
    // One row beyond the limit decides the preflight; never scan further.
    if (columnCount > COLUMN_LIMIT || dataRowCount > ROW_LIMIT) break;
    if (dataRowCount % 500 === 0)
      onProgress?.({
        phase: 'csv-reading',
        fileName: file.name,
        processedRows: dataRowCount,
      });
    for (const [index, value] of row.entries()) {
      if (value.trim() === '') continue;
      let state = types[index];
      if (!state) {
        state = {
          count: 0,
          checkbox: true,
          number: true,
          date: true,
          link: true,
        };
        types[index] = state;
      }
      if (state.count >= TYPE_SAMPLE_ROW_LIMIT) continue;
      state.count++;
      state.checkbox &&= isCheckbox(value);
      state.number &&= isNumber(value);
      state.date &&= isDate(value);
      state.link &&= isUrl(value);
    }
    const rowBytes = encoder.encode(JSON.stringify(row)).byteLength;
    if (
      previewRows.length < PREVIEW_ROW_LIMIT &&
      previewBytes + rowBytes <= 65536
    ) {
      previewRows.push(row);
      previewBytes += rowBytes;
    }
    if (
      typeSampleRows.length < TYPE_SAMPLE_ROW_LIMIT &&
      sampleBytes + rowBytes <= 65536
    ) {
      typeSampleRows.push(row);
      sampleBytes += rowBytes;
    }
  }
  const source = file.name;
  onProgress?.({
    phase: 'preflight-complete',
    fileName: file.name,
    processedRows: dataRowCount,
    totalRows: dataRowCount,
  });
  const rootIndexBytes =
    dataRowCount * (IMPORT_ROW_ID_LENGTH + 40) +
    columnCount * 512 +
    Math.ceil(file.size / (256 * 1024)) * 1024;
  const limit: ImportLimitDetails | undefined =
    shapeLimit(dataRowCount, columnCount) ??
    (rootIndexBytes > DOCUMENT_PRODUCT_HARD_LIMIT_BYTES
      ? {
          kind: 'root-index-bytes',
          actual: rootIndexBytes,
          limit: DOCUMENT_PRODUCT_HARD_LIMIT_BYTES,
        }
      : undefined);
  return {
    source,
    fileName: file.name,
    limit,
    columns: createColumnsFromSamples(
      headerRow,
      Math.min(columnCount, COLUMN_LIMIT),
      typeSampleRows,
      Array.from(
        {
          length: Math.min(columnCount, COLUMN_LIMIT),
        },
        (_, index) => {
          const state = types[index];
          return !state?.count
            ? 'rich-text'
            : state.checkbox
              ? 'checkbox'
              : state.number
                ? 'number'
                : state.date
                  ? 'date'
                  : state.link
                    ? 'link'
                    : 'rich-text';
        }
      )
    ),
    previewRows,
    estimate: estimateDatabaseImportPreflight({
      source,
      rowCount: dataRowCount,
      columnCount,
      fileBytes: file.size,
      sampleRows: typeSampleRows,
      // Payloads live in shards, but every row address remains in the root.
      streamingRootIndexBytes: rootIndexBytes,
    }),
  };
}

export async function preflightExcelFile({
  file,
  hasHeader = true,
  selectedSheets,
  onProgress,
}: ExcelPreflightOptions): Promise<ImportFilePreflight> {
  onProgress?.({ phase: 'file-reading', fileName: file.name });
  const arrayBuffer = await readExcelWorkbookBytes(file);
  onProgress?.({ phase: 'workbook-reading', fileName: file.name });
  const workbook = await excelWorkbookReader.read(arrayBuffer, {
    maxRowsToRead: EXCEL_ROWS_TO_READ,
    maxColumnsToRead: EXCEL_COLUMNS_TO_READ,
  });
  onProgress?.({ phase: 'workbook-loaded', fileName: file.name });
  const sheetNames = workbook.map(sheet => sheet.name);
  const sheetsToInspect = selectedSheets ?? sheetNames;
  const sheets: ImportSheetPreflight[] = [];

  for (const sheetName of sheetsToInspect) {
    if (!sheetNames.includes(sheetName)) {
      throw new Error(`Sheet not found in "${file.name}": ${sheetName}`);
    }

    const worksheet = workbook.find(sheet => sheet.name === sheetName);
    if (!worksheet) continue;

    onProgress?.({
      phase: 'sheet-scanning',
      fileName: file.name,
      sheetName,
    });
    const columnCount = worksheet.columnCount;
    const previewColumnCount = Math.min(columnCount, COLUMN_LIMIT);
    const rowCount = Math.max(worksheet.rowCount - (hasHeader ? 1 : 0), 0);
    const rawSampleRows = worksheet.rows.slice(
      0,
      PREVIEW_ROW_LIMIT + (hasHeader ? 1 : 0)
    );
    const headerRow = hasHeader ? (rawSampleRows[0] ?? null) : null;
    const sampleDataRows = (
      hasHeader ? rawSampleRows.slice(1) : rawSampleRows
    ).slice(0, TYPE_SAMPLE_ROW_LIMIT);
    const source = `${file.name} / ${sheetName}`;

    sheets.push({
      source,
      fileName: file.name,
      sheetName,
      limit: shapeLimit(rowCount, columnCount),
      columns: createColumnsFromSamples(
        headerRow,
        previewColumnCount,
        sampleDataRows
      ),
      previewRows: stringifyRows(sampleDataRows.slice(0, PREVIEW_ROW_LIMIT)),
      estimate: estimateDatabaseImportPreflight({
        source,
        rowCount,
        columnCount,
        fileBytes: file.size,
        sampleRows: sampleDataRows,
      }),
    });
  }

  onProgress?.({
    phase: 'preflight-complete',
    fileName: file.name,
    totalRows: sheets.reduce((sum, sheet) => sum + sheet.estimate.rowCount, 0),
  });

  return {
    fileName: file.name,
    sheets,
  };
}

export function assertImportPreflightAllowsInlineShardedMutation(
  preflights: readonly ImportSheetPreflight[]
): void {
  for (const preflight of preflights) {
    assertDatabaseImportPreflightAllowsInlineShardedMutation(
      preflight.estimate
    );
  }
}

export function assertImportPreflightAllowsShardedMutation(
  preflights: readonly ImportSheetPreflight[]
): void {
  for (const preflight of preflights) {
    if (preflight.limit) throw new ImportLimitError(preflight.limit);
    assertDatabaseImportPreflightAllowsShardedMutation(preflight.estimate);
  }
}
